/**
 * Utility functions for parsing and handling emoji content in chat messages
 */

export interface ParsedEmoji {
  type: 'text' | 'emoji' | 'custom-emoji'
  content: string
  imageUrl?: string
  alt?: string
  dataId?: string
}

/**
 * Parses message content to extract emoji data and text
 * @param content - The HTML content of the message
 * @returns Array of parsed content segments
 */
export function parseMessageContent(content: string): ParsedEmoji[] {
  if (!content) return []

  const segments: ParsedEmoji[] = []

  // Regular expression to match emoji img tags
  const emojiRegex = /<img[^>]*class="emoji"[^>]*>/gi

  let lastIndex = 0
  let match

  while ((match = emojiRegex.exec(content)) !== null) {
    // Add text before the emoji
    if (match.index > lastIndex) {
      const textContent = content.substring(lastIndex, match.index).trim()
      if (textContent) {
        segments.push({
          type: 'text',
          content: textContent,
        })
      }
    }

    // Parse the emoji img tag
    const imgTag = match[0]
    const emojiData = parseEmojiImgTag(imgTag)
    if (emojiData) {
      segments.push(emojiData)
    }

    lastIndex = match.index + match[0].length
  }

  // Add remaining text after the last emoji
  if (lastIndex < content.length) {
    const textContent = content.substring(lastIndex).trim()
    if (textContent) {
      segments.push({
        type: 'text',
        content: textContent,
      })
    }
  }

  return segments
}

/**
 * Parses message content to extract all img tags and text
 * @param content - The HTML content of the message
 * @returns Array of parsed content segments
 */
export function parseAllImgContent(content: string): ParsedEmoji[] {
  if (!content) return []

  const segments: ParsedEmoji[] = []

  // Regular expression to match all img tags
  const imgRegex = /<img[^>]*>/gi

  let lastIndex = 0
  let match

  while ((match = imgRegex.exec(content)) !== null) {
    // Add text before the img
    if (match.index > lastIndex) {
      const textContent = content.substring(lastIndex, match.index).trim()
      if (textContent) {
        segments.push({
          type: 'text',
          content: textContent,
        })
      }
    }

    // Parse the img tag
    const imgTag = match[0]
    const imgData = parseImgTag(imgTag)
    if (imgData) {
      segments.push(imgData)
    }

    lastIndex = match.index + match[0].length
  }

  // Add remaining text after the last img
  if (lastIndex < content.length) {
    const textContent = content.substring(lastIndex).trim()
    if (textContent) {
      segments.push({
        type: 'text',
        content: textContent,
      })
    }
  }

  return segments
}

/**
 * Parses an emoji img tag to extract its data
 * @param imgTag - The HTML img tag string
 * @returns Parsed emoji data or null if invalid
 */
function parseEmojiImgTag(imgTag: string): ParsedEmoji | null {
  try {
    // Extract src attribute
    const srcMatch = imgTag.match(/src="([^"]*)"/)
    const src = srcMatch ? srcMatch[1] : ''

    // Extract alt attribute
    const altMatch = imgTag.match(/alt="([^"]*)"/)
    const alt = altMatch ? altMatch[1] : ''

    // Extract data-id attribute
    const dataIdMatch = imgTag.match(/data-id="([^"]*)"/)
    const dataId = dataIdMatch ? dataIdMatch[1] : ''

    // Check if it's a custom emoji (has data-id) or regular emoji
    const isCustomEmoji = !!dataId

    return {
      type: isCustomEmoji ? 'custom-emoji' : 'emoji',
      content: alt || src,
      imageUrl: src,
      alt,
      dataId: dataId || undefined,
    }
  } catch (error) {
    // Silently handle parsing errors
    return null
  }
}

/**
 * Parses any img tag to extract its data
 * @param imgTag - The HTML img tag string
 * @returns Parsed img data or null if invalid
 */
function parseImgTag(imgTag: string): ParsedEmoji | null {
  try {
    // Extract src attribute
    const srcMatch = imgTag.match(/src="([^"]*)"/)
    const src = srcMatch ? srcMatch[1] : ''

    // Extract alt attribute
    const altMatch = imgTag.match(/alt="([^"]*)"/)
    const alt = altMatch ? altMatch[1] : ''

    // Extract data-id attribute
    const dataIdMatch = imgTag.match(/data-id="([^"]*)"/)
    const dataId = dataIdMatch ? dataIdMatch[1] : ''

    // Check if it's an emoji (has class="emoji")
    const isEmoji = imgTag.includes('class="emoji"')

    if (isEmoji) {
      // Check if it's a custom emoji (has data-id) or regular emoji
      const isCustomEmoji = !!dataId
      return {
        type: isCustomEmoji ? 'custom-emoji' : 'emoji',
        content: alt || src,
        imageUrl: src,
        alt,
        dataId: dataId || undefined,
      }
    } else {
      // Regular image
      return {
        type: 'emoji', // Use 'emoji' type for regular images too
        content: alt || src,
        imageUrl: src,
        alt,
        dataId: dataId || undefined,
      }
    }
  } catch (error) {
    // Silently handle parsing errors
    return null
  }
}

/**
 * Checks if content contains any img tags
 * @param content - The HTML content to check
 * @returns True if content contains any img tags
 */
export function hasImgTags(content: string): boolean {
  if (!content) return false
  return /<img[^>]*>/gi.test(content)
}

/**
 * Checks if content contains any emoji images
 * @param content - The HTML content to check
 * @returns True if content contains emoji images
 */
export function hasEmojiImages(content: string): boolean {
  if (!content) return false
  return /<img[^>]*class="emoji"[^>]*>/gi.test(content)
}

/**
 * Gets a preview of emoji content for display in reply previews
 * @param content - The HTML content
 * @param maxLength - Maximum length for text content
 * @returns Preview text with emoji indicators
 */
export function getEmojiPreview(content: string, maxLength = 30): string {
  if (!content) return ''

  const segments = parseMessageContent(content)
  let preview = ''

  for (const segment of segments) {
    if (segment.type === 'text') {
      preview += segment.content
    } else if (segment.type === 'custom-emoji') {
      preview += `[${segment.alt || 'Custom Emoji'}]`
    } else if (segment.type === 'emoji') {
      preview += segment.content
    }

    if (preview.length >= maxLength) {
      break
    }
  }

  return preview.length > maxLength ? preview.substring(0, maxLength) + '...' : preview
}
